import 'vite/modulepreload-polyfill';
import React, { useSyncExternalStore, useState, useEffect } from 'react';
import { lazyComponents } from '@src/utilities/LazyComponent';
import { useAnimation } from '@src/hooks/useAnimation';
import useColoredTextParser from '@src/hooks/useColoredTextParser';
import useMediaQuery from '@src/hooks/useMediaQuery';
import useCurrentTextStyle from '@src/hooks/useCurrentTextStyle';
import { useEnhancedBlockEvents } from '../src/hooks/useEnhancedBlockEvents.jsx';
import { useBlockPositioning } from '../src/hooks/useBlockPositioning.jsx';
import { useBlockSettings, useBlockAnimations, BlockWrapper } from '../src/hooks/useBlockSettings.jsx';
import { withBlockEnhancements, useInputEvents } from '../src/utils/blockEnhancements.jsx';
import useMobileBreakpoint from '@src/hooks/useMobileBreakpoint.jsx';

export const TagListComponent = withBlockEnhancements(
  ({ settings }) => {
    const TagList = lazyComponents['TagList'];
    return <TagList tags={settings.tags} />;
  },
  { enableEvents: false, enableAnimations: false, enablePositioning: false }
);
TagListComponent.displayName = 'theme__block-tags';

export const SocialLinksComponent = withBlockEnhancements(
  ({ settings }) => {
    const SocialLinks = lazyComponents['SocialLinks'];
    const social_links = settings.shop.social_links;
    const section_settings = settings.settings;

    const links = Object.keys(social_links).map((key) => ({
      url: social_links[key],
      name: key
    }));

    return (
      <SocialLinks
        type={'default'}
        links={links}
        settings={section_settings}
      />
    );
  },
  { enableEvents: false, enableAnimations: false, enablePositioning: false }
);
SocialLinksComponent.displayName = 'theme__social-links';

export const LogoComponent = withBlockEnhancements(
  ({ settings }) => {
    const Logo = lazyComponents['Logo'];
    return <Logo settings={settings} />;
  },
  { enableEvents: false, enableAnimations: false, enablePositioning: false }
);
LogoComponent.displayName = 'theme__block-logo';

export const CartItemComponent = withBlockEnhancements(
  ({ settings }) => {
    const CartItem = lazyComponents['CartItem'];
    return <CartItem settings={settings} />;
  },
  { enableEvents: true, enableAnimations: true, enablePositioning: false }
);
CartItemComponent.displayName = 'theme__block-cart-item';

export const TitleComponent = withBlockEnhancements(
  ({ settings }) => {
    const Title = lazyComponents['Title'];
    return <Title settings={settings} />;
  },
  { enableEvents: false, enableAnimations: false, enablePositioning: false }
);
TitleComponent.displayName = 'theme__block-title';

export const DescriptionComponent = withBlockEnhancements(
  ({ settings }) => {
    const Description = lazyComponents['Description'];
    return <Description settings={settings} />;
  },
  { enableEvents: false, enableAnimations: false, enablePositioning: false }
);
DescriptionComponent.displayName = 'theme__block-description';

export function ButtonComponent({ ...props }) {
  const Button = lazyComponents['Button'];

  // Use consolidated hooks
  const { blockSettings, hasSettings } = useBlockSettings(props);

  if (!hasSettings) {
    console.warn('ButtonComponent: No block settings found');
    return null;
  }

  // Use enhanced event system
  const events = useEnhancedBlockEvents(blockSettings, blockSettings.button_text || 'button', 'button');
  const positioning = useBlockPositioning(blockSettings);
  const animations = useBlockAnimations(blockSettings);
  const animation = useAnimation(animations.animationProps);

  // Extract button-specific settings
  const buttonText = blockSettings.button_text || '';
  const buttonUrl = blockSettings.button_url || props.settings.override_url || null;
  const buttonStyle = blockSettings.button_style.toLowerCase() || 'primary';
  const buttonSize =
    blockSettings.cta_style_type === 'link' ? blockSettings.link_size || 'md' : blockSettings.button_size || 'md';
  const buttonIcon = blockSettings.button_icon || '';
  const buttonReverseIcon = blockSettings.button_reverse_icon || false;
  const buttonType = blockSettings.button_type || 'button';
  const buttonStyleType = blockSettings.cta_style_type || 'button';
  const buttonGap = blockSettings.button_gap || 8;

  // Enhanced click handler with actions
  const handleButtonClick = (e) => {
    e.preventDefault();
    // Handle URL navigation if no actions are configured
    if (!events.enableActions && buttonUrl) {
      if (buttonUrl.startsWith('http') || buttonUrl.startsWith('//')) {
        window.open(buttonUrl, '_blank');
      } else {
        window.location.href = buttonUrl;
      }
    } else {
      if (buttonUrl) {
        window.location.href = buttonUrl;
      }
    }

    // Trigger configured actions
    events.handleClick({
      buttonText,
      buttonUrl,
      buttonStyle,
      clickTarget: e?.target
    });
  };

  return (
    <BlockWrapper
      blockSettings={blockSettings}
      animation={animation}
      positionProps={positioning.positionProps}
      eventHandlers={{
        handleClick: (e) => handleButtonClick(e),
        handleMouseEnter: events.handleMouseEnter,
        handleMouseLeave: events.handleMouseLeave,
        handleFocus: events.handleFocus,
        handleBlur: events.handleBlur
      }}
    >
      <Button
        settings={props.settings}
        label={buttonText}
        link={events.enableActions ? null : buttonUrl} // Don't use link if actions are enabled
        style={buttonStyle}
        styleType={buttonStyleType}
        size={buttonSize}
        icon={buttonIcon}
        reverse={buttonReverseIcon}
        type={buttonType}
        onClick={(e) => handleButtonClick(e)}
        className={blockSettings.button_classes || ''}
        isLoading={events.isLoading}
        gap={buttonGap}
      />
    </BlockWrapper>
  );
}
ButtonComponent.displayName = 'theme__block-button';

export function CurrencySelectorComponent({ ...props }) {
  const CurrencySelector = lazyComponents['CurrencySelector'];

  // Use consolidated hooks
  const { blockSettings, hasSettings } = useBlockSettings(props);

  if (!hasSettings) {
    return null;
  }

  // Initialize common functionality
  const positioning = useBlockPositioning(blockSettings);
  const animations = useBlockAnimations(blockSettings);
  const animation = useAnimation(animations.animationProps);

  return (
    <BlockWrapper
      blockSettings={blockSettings}
      animation={animation}
      positionProps={positioning.positionProps}
    >
      <CurrencySelector
        displayStyle={blockSettings.display_style || 'dropdown'}
        showCurrencySymbol={blockSettings.show_currency_symbol !== false}
        showCurrencyCode={blockSettings.show_currency_code !== false}
        showCurrencyName={blockSettings.show_currency_name || false}
        showFlagIcons={blockSettings.show_flag_icons !== false}
        selectorLabel={blockSettings.selector_label || 'Currency:'}
        hideLabel={blockSettings.hide_label || false}
        autoRedirect={blockSettings.auto_redirect !== false}
        rememberSelection={blockSettings.remember_selection !== false}
        fallbackCurrency={blockSettings.fallback_currency || 'USD'}
        enableAnimations={animations.animationProps.enableAnimations}
        animationType={animations.animationProps.animationType}
        animationDuration={animations.animationProps.animationDuration}
        animationDelay={animations.animationProps.animationDelay}
        animationTrigger={animations.animationProps.animationTrigger}
        settings={blockSettings}
      />
    </BlockWrapper>
  );
}
CurrencySelectorComponent.displayName = 'theme__currency-selector';

export function TextComponent({ ...props }) {
  const Paragraph = lazyComponents['Paragraph'];
  const blockId = props.settings.block_id;
  const blockData = props.settings.block_settings;

  const { getCurrentTextStyle } = useCurrentTextStyle();

  const text = blockData?.text || props.settings.override_text;

  // Get responsive text style settings
  const baseStyle = blockData.text_style || 'b1';
  const enableResponsiveStyles = blockData.text_style_enable_responsive || false;
  const styleMobile = blockData.text_style_mobile || '';
  const styleTablet = blockData.text_style_tablet || '';
  const styleUltrawide = blockData.text_style_ultrawide || '';

  const currentStyle = getCurrentTextStyle(baseStyle, enableResponsiveStyles, styleMobile, styleTablet, styleUltrawide);

  // Get smart header settings
  const enableSmartHeaders = blockData.enable_smart_headers !== false; // Default to true
  const htmlTagOverride = blockData.html_tag || 'auto';

  // Function to determine the appropriate HTML tag based on text style
  const getSmartHtmlTag = () => {
    // If there's an explicit override (not 'auto'), always use it regardless of smart headers setting
    if (htmlTagOverride !== 'auto') {
      return htmlTagOverride;
    }

    // If smart headers is disabled and no override is set, default to div
    if (!enableSmartHeaders) {
      return 'div';
    }

    // Smart logic: match header styles to HTML tags when smart headers is enabled and override is 'auto'
    const style = currentStyle.toLowerCase().trim();

    // Check for explicit header patterns
    if (
      style === 'h1' ||
      style.startsWith('h1-') ||
      style.endsWith('-h1') ||
      style.includes('heading-1') ||
      style.includes('header-1')
    ) {
      return 'h1';
    } else if (
      style === 'h2' ||
      style.startsWith('h2-') ||
      style.endsWith('-h2') ||
      style.includes('heading-2') ||
      style.includes('header-2')
    ) {
      return 'h2';
    } else if (
      style === 'h3' ||
      style.startsWith('h3-') ||
      style.endsWith('-h3') ||
      style.includes('heading-3') ||
      style.includes('header-3')
    ) {
      return 'h3';
    } else if (
      style === 'h4' ||
      style.startsWith('h4-') ||
      style.endsWith('-h4') ||
      style.includes('heading-4') ||
      style.includes('header-4')
    ) {
      return 'h4';
    } else if (
      style === 'h5' ||
      style.startsWith('h5-') ||
      style.endsWith('-h5') ||
      style.includes('heading-5') ||
      style.includes('header-5')
    ) {
      return 'h5';
    } else if (
      style === 'h6' ||
      style.startsWith('h6-') ||
      style.endsWith('-h6') ||
      style.includes('heading-6') ||
      style.includes('header-6')
    ) {
      return 'h6';
    }

    // Check for general heading patterns
    if (style.includes('heading') || style.includes('header') || style.includes('title')) {
      // Default to h2 for general heading styles
      return 'h2';
    }

    // For body text styles, captions, or anything else, use paragraph
    return 'p';
  };

  const smartHtmlTag = getSmartHtmlTag();

  // Debug logging - remove this after testing
  // console.log('Text Block Debug:', {
  //     currentStyle,
  //     enableSmartHeaders,
  //     htmlTagOverride,
  //     smartHtmlTag,
  //     blockId
  // });

  const maxChars = blockData.max_chars ? parseInt(blockData.max_chars, 10) : 0; // Get max chars with fallback to 0 (unlimited)

  // Get text color settings
  const overrideTextColor = blockData.override_text_color || false;
  const textColor = blockData.text_color || '';

  // Get text accent part settings
  const enableAccentParts = blockData.enable_accent_parts || false;
  const accentParts = blockData.accent_title_parts || '';
  const accentColor = blockData.accent_color || '#f26823';

  // Get RTE styles setting
  const enableRteStyles = blockData.enable_rte_styles || false;

  // Extract positioning data for overlay support
  const positionType = blockData.position_type || 'relative';
  const positionProps =
    positionType === 'absolute'
      ? {
          isOverlay: true,
          zIndex: blockData.z_index || 10
        }
      : {};

  // Animation settings
  const enableAnimations = blockData.enable_animations || false;
  const animationType = blockData.animation_type || 'slide-fade-up';
  const animationDuration = blockData.animation_duration || 800;
  const animationDelay = blockData.animation_delay || 0;
  const animationTrigger = blockData.animation_trigger || 'onscroll';

  // Use the animation hook
  const animation = useAnimation({
    enableAnimations,
    animationType,
    animationDuration,
    animationDelay,
    animationTrigger
  });

  // Define custom styles including text color if overridden
  const textStyles = overrideTextColor ? { color: textColor } : {};

  // Use the colored text parser if accent parts are enabled
  const renderFormattedText = useColoredTextParser({
    title: text,
    accentTitleParts: accentParts,
    accentColor: accentColor,
    headerStyle: currentStyle, // Use the dynamically determined style
    classes: '' // Don't use classes for text color, use style instead
  });

  const isNoWrap = blockData?.is_nowrap || false;
  textStyles.whiteSpace = isNoWrap ? 'nowrap' : 'normal';

  return (
    <div
      ref={animation.ref}
      style={animation.style}
    >
      {enableAccentParts ? (
        <div style={textStyles}>{renderFormattedText()}</div>
      ) : (
        <Paragraph
          text={text}
          type={currentStyle} // Use the dynamically determined style
          htmlTag={smartHtmlTag} // Use the smart HTML tag
          maxChars={maxChars || 0} // If maxChars is 0, it will be treated as unlimited
          shouldTruncateText={true}
          style={textStyles}
          enableRteStyles={enableRteStyles}
          readMorePosition={blockData.read_more_position || 'inline'}
          {...positionProps}
        />
      )}
    </div>
  );
}
TextComponent.displayName = 'theme__block-text';

export function VideoComponent({ ...props }) {
  const Video = lazyComponents['Video'];

  const blockId = props.settings.block_id;
  const blockSettings =
    props.settings.block_settings || props.settings.blocks.find((block) => block.id === blockId)?.settings;

  if (!blockSettings) {
    return null;
  }

  // Get responsive video settings
  const enableResponsiveVideos = blockSettings.enable_responsive_videos || false;
  const defaultVideo = blockSettings.video;
  const videoMobile = blockSettings.video_mobile;
  const videoTablet = blockSettings.video_tablet;
  const videoDesktop = blockSettings.video_desktop;
  const videoUltrawide = blockSettings.video_ultrawide;

  // Function to get video URL from video object
  const getVideoUrl = (video) => {
    return video?.sources?.find((source) => source.format === 'mp4')?.url || '';
  };

  // Get video URLs for all breakpoints
  const videoUrls = {
    default: getVideoUrl(defaultVideo),
    mobile: enableResponsiveVideos ? getVideoUrl(videoMobile) : '',
    tablet: enableResponsiveVideos ? getVideoUrl(videoTablet) : '',
    desktop: enableResponsiveVideos ? getVideoUrl(videoDesktop) : '',
    ultrawide: enableResponsiveVideos ? getVideoUrl(videoUltrawide) : ''
  };

  // If no default video, return null
  if (!videoUrls.default) {
    return null;
  }

  // Extract all video settings
  const sizingMode = blockSettings.sizing_mode || 'aspect_ratio';
  const aspectRatio = blockSettings.aspect_ratio || '16:9';
  const customHeight = blockSettings.custom_height || 0;
  const objectFit = blockSettings.object_fit || 'cover';
  const autoplay = blockSettings.autoplay || false;
  const loop = blockSettings.loop || false;
  const controls = blockSettings.controls !== false;
  const muted = blockSettings.muted !== false;

  // Extract border radius settings
  const baseBorderRadius = blockSettings.border_radius || 0;
  const enableResponsiveBorderRadius = blockSettings.border_radius_enable_responsive || false;
  const borderRadiusMobile = blockSettings.border_radius_mobile || 0;
  const borderRadiusTablet = blockSettings.border_radius_tablet || 0;
  const borderRadiusUltrawide = blockSettings.border_radius_ultrawide || 0;

  // Get breakpoints from global settings
  const breakpoints = props.settings.globals?.responsive_breakpoints || {
    mobile_max: 767,
    tablet_min: 768,
    tablet_max: 1023,
    desktop_min: 1024,
    desktop_max: 1439,
    ultrawide_min: 1440
  };

  // Use media queries to determine current breakpoint
  const isMobile = useMediaQuery(null, breakpoints.mobile_max);
  const isTablet = useMediaQuery(breakpoints.tablet_min, breakpoints.tablet_max);
  const isDesktop = useMediaQuery(breakpoints.desktop_min, breakpoints.desktop_max);
  const isUltrawide = useMediaQuery(breakpoints.ultrawide_min, null);

  // Determine the correct border radius based on current breakpoint and responsive settings
  const getCurrentBorderRadius = () => {
    if (!enableResponsiveBorderRadius) {
      return baseBorderRadius;
    }

    if (isUltrawide && borderRadiusUltrawide > 0) {
      return borderRadiusUltrawide;
    }

    if (isDesktop) {
      return baseBorderRadius; // Desktop uses the base border radius
    }

    if (isTablet && borderRadiusTablet > 0) {
      return borderRadiusTablet;
    }

    if (isMobile && borderRadiusMobile > 0) {
      return borderRadiusMobile;
    }

    // Fallback to base border radius
    return baseBorderRadius;
  };

  const currentBorderRadius = getCurrentBorderRadius();

  // Extract positioning data for overlay support
  const positionType = blockSettings.position_type || 'relative';
  const positionProps =
    positionType === 'absolute'
      ? {
          isOverlay: true,
          zIndex: blockSettings.z_index || 5
        }
      : {};

  // Animation settings
  const enableAnimations = blockSettings.enable_animations || false;
  const animationType = blockSettings.animation_type || 'slide-fade-up';
  const animationDuration = blockSettings.animation_duration || 800;
  const animationDelay = blockSettings.animation_delay || 0;
  const animationTrigger = blockSettings.animation_trigger || 'onscroll';

  // Use the animation hook
  const animation = useAnimation({
    enableAnimations,
    animationType,
    animationDuration,
    animationDelay,
    animationTrigger
  });

  return (
    <div
      ref={animation.ref}
      style={animation.style}
    >
      <Video
        videoUrls={videoUrls}
        enableResponsiveVideos={enableResponsiveVideos}
        sizingMode={sizingMode}
        aspectRatio={aspectRatio}
        customHeight={customHeight}
        objectFit={objectFit}
        autoplay={autoplay}
        loop={loop}
        controls={controls}
        muted={muted}
        playsInline={true}
        borderRadius={currentBorderRadius}
        {...positionProps}
      />
    </div>
  );
}
VideoComponent.displayName = 'theme__block-video';

export function ImageComponent({ ...props }) {
  const Image = lazyComponents['Image'];

  // Use consolidated hooks
  const { blockSettings, hasSettings } = useBlockSettings(props);

  if (!hasSettings) {
    console.warn('ImageComponent: No block settings found');
    return null;
  }

  // Use enhanced event system
  const events = useEnhancedBlockEvents(blockSettings, 'image', 'image');
  const positioning = useBlockPositioning(blockSettings);
  const animations = useBlockAnimations(blockSettings);
  const animation = useAnimation(animations.animationProps);

  // Extract image-specific settings
  const imageUrl = blockSettings.image || props.settings.override_image || '';
  const isPriority = blockSettings.is_priority || false; // Use is_priority setting
  const responsiveImages = props.settings.responsive_images || {};
  const imageFit = blockSettings.image_fit || 'cover';
  const aspectRatio = blockSettings.aspect_ratio || 'auto';
  const overlayOpacity = blockSettings.overlay_opacity || 0;
  const borderRadius = blockSettings.border_radius || 0;
  const height = blockSettings.height || undefined; // <-- NEW: get height from settings

  // Extract link settings
  const enableLink = blockSettings.enable_link ? false : true || props.settings.override_url ? true : false;
  const link = blockSettings.link || props.settings.override_url || '';
  const linkTarget = blockSettings.link_target || '_self';
  const hasLink = enableLink && link.trim() !== '';

  // Enhanced click handler with actions
  const handleImageClick = () => {
    // Handle URL navigation if no actions are configured
    if (!events.enableActions && hasLink) {
      if (linkTarget === '_blank' || link.startsWith('http') || link.startsWith('//')) {
        window.open(link, linkTarget);
      } else {
        window.location.href = link;
      }
    }

    // Trigger configured actions
    events.handleClick({
      image_url: imageUrl,
      link: link,
      has_link: hasLink
    });
  };

  const styles = {
    ...(blockSettings.width < 100 ? { width: `${blockSettings.width}%` } : {}),
    ...(blockSettings.enable_hover_animations
      ? { transition: `all ${blockSettings?.hover_animation_duration || 300}ms ease-in-out` }
      : {})
  };

  const classes = `${blockSettings.width < 100 ? '' : 'w-full'} ${
    blockSettings.enable_hover_animations ? `${blockSettings?.hover_animation_type || ''} will-change-transform` : ''
  }`;

  const imageComponent = (
    <Image
      imageUrl={imageUrl}
      isPriority={isPriority}
      responsiveImages={responsiveImages}
      fit={imageFit}
      ratio={aspectRatio}
      overlay={overlayOpacity > 0}
      overlayOpacity={overlayOpacity / 100}
      borderRadius={borderRadius}
      height={height} // <-- NEW: pass height
      classes={classes}
      styles={styles}
    />
  );

  return (
    <BlockWrapper
      blockSettings={blockSettings}
      animation={animation}
      positionProps={positioning.positionProps}
      eventHandlers={{
        handleClick: handleImageClick,
        handleMouseEnter: events.handleMouseEnter,
        handleMouseLeave: events.handleMouseLeave
      }}
    >
      {hasLink ? (
        <a
          href={link}
          target={linkTarget}
          rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
          className='block'
        >
          {imageComponent}
        </a>
      ) : (
        <div>{imageComponent}</div>
      )}
    </BlockWrapper>
  );
}
ImageComponent.displayName = 'theme__block-image';

export function GroupComponent({ ...props }) {
  const blockId = props.settings.block_id;
  const blockData = props.settings.blocks.find((block) => block.id === blockId);
  const blockSettings = props.settings.block_settings || {};

  const size = blockSettings.size || {};
  const spacing = blockSettings.spacing || {};

  // Convert size and spacing to style object
  const styleObj = {
    width: size.width || '100%',
    height: size.height || 'auto',
    padding: spacing.padding || '0px',
    margin: spacing.margin || '0px'
  };

  // Animation settings
  const enableAnimations = blockSettings.enable_animations || false;
  const animationType = blockSettings.animation_type || 'slide-fade-up';
  const animationDuration = blockSettings.animation_duration || 800;
  const animationDelay = blockSettings.animation_delay || 0;
  const animationTrigger = blockSettings.animation_trigger || 'onscroll';

  // Use the animation hook
  const animation = useAnimation({
    enableAnimations,
    animationType,
    animationDuration,
    animationDelay,
    animationTrigger
  });

  // Find all child blocks within this group
  const childBlocks = blockData?.blocks || [];

  return (
    <div
      ref={animation.ref}
      style={{ ...styleObj, ...animation.style }}
      className='group-component'
    >
      {childBlocks.map((block, index) => {
        // Render child blocks here
        return <div key={`block-${index}`}></div>;
      })}
    </div>
  );
}

InputComponent.displayName = 'theme__block-input';
export function InputComponent({ ...props }) {
  const InputField = lazyComponents['InputField'];
  const [inputValue, setInputValue] = React.useState('');
  const [debouncedValue, setDebouncedValue] = React.useState('');
  const debounceTimerRef = React.useRef(null);

  const blockId = props.settings.block_id;
  const blockData = props.settings.block_settings;

  if (!blockData) {
    return null;
  }

  // Extract input settings
  const inputType = blockData.input_type || 'text';
  const inputName = blockData.input_name || '';
  const inputId = blockData.input_id || '';
  const inputLabel = blockData.input_label || '';
  const inputPlaceholder = blockData.input_placeholder || '';
  const inputRequired = blockData.input_required === 'true' || blockData.input_required === true;
  const inputPattern = blockData.input_pattern || '';
  const inputOptions = blockData.input_options || '';

  // Event tracking settings
  const enableEventTracking = blockData.enable_event_tracking || false;
  const eventTypeMapping = {
    change: 'input_change',
    blur: 'input_blur',
    focus: 'input_focus',
    keypress: 'input_keypress',
    finished: 'input_finished'
  };
  const eventName = blockData.event_name || eventTypeMapping.finished;
  const eventCategory = blockData.event_category || 'interaction';
  const eventLabel = blockData.event_label || inputLabel || inputName;
  const eventValue = blockData.event_value ? Number(blockData.event_value) : undefined;
  const debounceDelay = parseInt(blockData.debounce_delay || 500, 10);

  // Function to handle input events and dispatch a CustomEvent with debounce
  const handleInputEvent = (eventType, value) => {
    if (!enableEventTracking) return;

    if (eventType === 'change') {
      // Update the current input value immediately
      setInputValue(value);

      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set a new timer for the "finished typing" event
      debounceTimerRef.current = setTimeout(() => {
        setDebouncedValue(value);

        // Only dispatch the "finished" event when the user has stopped typing
        if (typeof window !== 'undefined') {
          try {
            window.dispatchEvent(
              new CustomEvent(eventName, {
                detail: {
                  type: eventCategory,
                  label: eventLabel,
                  query: value,
                  inputName: inputName,
                  inputId: inputId,
                  eventType: 'finished'
                }
              })
            );
            console.log('Dispatched input finished event:', eventName, {
              value: value,
              inputName: inputName
            });
          } catch (error) {
            console.error('Error dispatching input event:', error);
          }
        }
      }, debounceDelay);
    } else {
      // For other events (blur, focus, keypress), dispatch immediately if configured
      const eventToDispatch = blockData[`enable_${eventType}_event`]
        ? blockData[`${eventType}_event_name`] || eventTypeMapping[eventType]
        : null;

      if (eventToDispatch && typeof window !== 'undefined') {
        try {
          window.dispatchEvent(
            new CustomEvent(eventToDispatch, {
              detail: {
                category: blockData[`${eventType}_event_category`] || eventCategory,
                label: blockData[`${eventType}_event_label`] || eventLabel,
                value: value,
                inputName: inputName,
                inputId: inputId,
                eventType: eventType
              }
            })
          );
        } catch (error) {
          console.error(`Error dispatching ${eventType} event:`, error);
        }
      }
    }
  };

  // Extract positioning data for overlay support
  const positionType = blockData.position_type || 'relative';
  const positionProps =
    positionType === 'absolute'
      ? {
          isOverlay: true,
          zIndex: blockData.z_index || 10
        }
      : {};

  // Parse options for select/radio/checkbox
  let parsedOptions = [];
  if (inputOptions && ['select', 'radio', 'checkbox'].includes(inputType)) {
    parsedOptions = inputOptions.split('\n').map((option) => {
      const parts = option.split('|');
      return {
        value: parts[0]?.trim() || '',
        label: parts[1]?.trim() || parts[0]?.trim() || ''
      };
    });
  }

  // For hidden fields, render a simple hidden input
  if (inputType === 'hidden') {
    return (
      <input
        type='hidden'
        name={inputName}
        id={inputId}
        value={inputValue}
        data-hidden-field='true'
      />
    );
  }

  // Animation settings
  const enableAnimations = blockData.enable_animations || false;
  const animationType = blockData.animation_type || 'slide-fade-up';
  const animationDuration = blockData.animation_duration || 800;
  const animationDelay = blockData.animation_delay || 0;
  const animationTrigger = blockData.animation_trigger || 'onscroll';

  // Use the animation hook
  const animation = useAnimation({
    enableAnimations,
    animationType,
    animationDuration,
    animationDelay,
    animationTrigger
  });

  // Properties for handling input events
  const inputEventProps = {
    onChange: (e) => handleInputEvent('change', e.target.value),
    onBlur: (e) => handleInputEvent('blur', e.target.value),
    onFocus: (e) => handleInputEvent('focus', e.target.value),
    onKeyPress: (e) => handleInputEvent('keypress', e.target.value),
    onKeyDown: (e) => {
      if (e.key === 'Enter') {
        // Cancel debounce and dispatch finished event immediately
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        setDebouncedValue(e.target.value);
        if (typeof window !== 'undefined') {
          try {
            window.dispatchEvent(
              new CustomEvent(eventName, {
                detail: {
                  type: eventCategory,
                  label: eventLabel,
                  query: e.target.value,
                  inputName: inputName,
                  inputId: inputId,
                  eventType: 'finished',
                  triggeredBy: 'enter-key'
                }
              })
            );
            console.log('Dispatched input finished event (Enter key):', eventName, {
              value: e.target.value,
              inputName: inputName
            });
          } catch (error) {
            console.error('Error dispatching input event (Enter key):', error);
          }
        }
      }
    }
  };

  return (
    <div
      ref={animation.ref}
      style={animation.style}
    >
      <InputField
        type={inputType}
        name={inputName}
        id={inputId}
        label={inputLabel}
        placeholder={inputPlaceholder}
        defaultValue={inputValue}
        required={inputRequired}
        pattern={inputPattern}
        options={parsedOptions}
        eventHandlers={enableEventTracking ? inputEventProps : {}}
        {...positionProps}
      />
    </div>
  );
}

IconComponent.displayName = 'theme__block-icon';
export function IconComponent({ ...props }) {
  const Icon = lazyComponents['Icon'];

  // Use consolidated hooks
  const { blockSettings, hasSettings } = useBlockSettings(props);
  const { globals } = props.settings;

  const isMobile = useMobileBreakpoint(globals?.responsive_breakpoints.mobile_max || 767);

  if (!hasSettings) {
    console.warn('IconComponent: No block settings found');
    return null;
  }

  // Use enhanced event system
  const events = useEnhancedBlockEvents(blockSettings, blockSettings.icon_name || 'icon', 'icon');
  const positioning = useBlockPositioning(blockSettings);
  const animations = useBlockAnimations(blockSettings);
  const animation = useAnimation(animations.animationProps);

  // Extract icon-specific settings
  const iconSource = blockSettings.icon_source || 'icon_name';
  const iconName = blockSettings.icon_name || 'star';
  const customIcon = blockSettings.custom_icon || '';
  const iconSize =
    isMobile && blockSettings?.override_icon_size_mobile
      ? blockSettings.icon_size_mobile
      : blockSettings.icon_size || 24;
  const iconLink = blockSettings.icon_link || '';

  // Enhanced click handler with actions
  const handleIconClick = (e) => {
    e.preventDefault();
    // Handle URL navigation if no actions are configured
    if (!events.enableActions && iconLink) {
      if (iconLink.startsWith('http') || iconLink.startsWith('//')) {
        window.open(iconLink, '_blank');
      } else {
        window.location.href = iconLink;
      }
    }

    // Trigger configured actions
    events.handleClick({
      icon_name: iconName,
      icon_source: iconSource,
      icon_link: iconLink
    });
  };

  return (
    <BlockWrapper
      blockSettings={blockSettings}
      animation={animation}
      positionProps={positioning.positionProps}
      eventHandlers={{
        handleClick: (e) => handleIconClick(e),
        handleMouseEnter: events.handleMouseEnter,
        handleMouseLeave: events.handleMouseLeave
      }}
      className='flex'
    >
      <Icon
        settings={blockSettings}
        iconSource={iconSource}
        iconName={iconName}
        customIcon={customIcon}
        iconSize={iconSize}
        iconLink={iconLink}
        onClick={(e) => handleIconClick(e)}
      />
    </BlockWrapper>
  );
}

BreadcrumbsComponent.displayName = 'theme__block-breadcrumbs';
export function BreadcrumbsComponent({ ...props }) {
  const Breadcrumbs = lazyComponents['Breadcrumbs'];
  const { getCurrentTextStyle } = useCurrentTextStyle();

  const blockSettings = props.settings.block_settings;

  if (!blockSettings) {
    return null;
  }

  const baseStyle = blockSettings.text_style || 'b2';
  const enableResponsiveStyles = blockSettings.text_style_enable_responsive || false;
  const styleMobile = blockSettings.text_style_mobile || '';
  const styleTablet = blockSettings.text_style_tablet || '';
  const styleUltrawide = blockSettings.text_style_ultrawide || '';

  const currentTextStyle = getCurrentTextStyle(
    baseStyle,
    enableResponsiveStyles,
    styleMobile,
    styleTablet,
    styleUltrawide
  );

  // Create a section object from block settings to match the format expected by the Breadcrumbs component
  const section = {
    divider_type: blockSettings.divider_type || 'icon',
    divider_text: blockSettings.divider_text || '/',
    spacing: blockSettings.spacing || 8,
    spacing_mobile: blockSettings.spacing_mobile || 4,
    icon: blockSettings.icon || 'icon-caret-right-thin',
    icon_size: blockSettings.icon_size || 18,
    text_style: currentTextStyle,
    text_style_mobile: blockSettings.text_style_mobile || 'b3',
    default_shop_url: blockSettings.default_shop_url || '',
    use_back_to_category_products: blockSettings.use_back_to_category_products || false,
    content_alignment: blockSettings.content_alignment || 'left'
  };

  // Create a settings object that matches what the Breadcrumbs component expects
  const breadcrumbSettings = {
    ...props.settings,
    section: section,
    mobile_breakpoint: props.settings.mobile_breakpoint || 768
  };

  return <Breadcrumbs settings={breadcrumbSettings} />;
}

AccordionComponent.displayName = 'theme__block-accordion';
export function AccordionComponent({ ...props }) {
  const Collapsible = lazyComponents['Collapsible'];
  const Paragraph = lazyComponents['Paragraph'];
  const Media = lazyComponents['Media'];
  const Button = lazyComponents['Button'];

  const blockSettings = props.settings.block_settings;
  const accordionItems = props.settings.blocks || [];

  if (!blockSettings) {
    return null;
  }

  const getAccordionItemClass = () => {
    let classes = [];

    if (blockSettings.vertical_spacing) {
      style.marginBottom = `${blockSettings.vertical_spacing}px`;
    }

    if (blockSettings.rounded_corners) {
      style.borderRadius = `${blockSettings.rounded_corners}px`;
    }

    // Add style-specific classes
    if (blockSettings.accordion_style) {
      switch (blockSettings.accordion_style) {
        case 'modern':
          classes.push('border border-gray-200 shadow-sm');
          break;
        case 'minimal':
          classes.push('border-b border-gray-100');
          break;
        case 'bordered':
          classes.push('border border-gray-300 p-4');
          break;
        default:
          break;
      }
    }

    return classes.join(' ');
  };

  // Find out if any accordion items have open_by_default set to true
  const hasDefaultOpenItem = accordionItems.some((item) => item.settings?.open_by_default);

  // Render each accordion item - could be directly defined or a nested accordion_item block
  const renderAccordionItem = (item, index) => {
    // Handle special case for accordion_item blocks
    if (item.type === 'accordion_item') {
      // Pass parent settings to the nested accordion item
      const nestedItemProps = {
        ...props,
        settings: {
          ...props.settings,
          block_settings: item.settings,
          parentSettings: blockSettings
        }
      };

      // Render the nested accordion item component directly
      return (
        <AccordionItemComponent
          key={`accordion_item_${index}`}
          {...nestedItemProps}
        />
      );
    }

    // Otherwise, render a standard accordion item from settings
    const titleAlignment = item.settings?.text_alignment || blockSettings.accordion_text_alignment || 'left';
    const iconPosition = blockSettings.icon_position || 'right';

    return (
      <Collapsible
        key={`accordion_item_${index}`}
        title={item.settings?.title}
        titleClass={`${blockSettings.title_font || 'bfb'} font-medium text-${titleAlignment}`}
        contentClass={`${blockSettings.content_font || 'b2'} text-${titleAlignment}`}
        openIcon={blockSettings.accordion_icon_open || 'icon-caret-down-thin'}
        closeIcon={blockSettings.accordion_icon_close || 'icon-caret-up-thin'}
        iconPosition={iconPosition}
        rotateIcon={blockSettings.rotate_icon || false}
        className={getAccordionItemClass()}
        background={blockSettings.accordion_background || 'transparent'}
        readMore={blockSettings.show_read_more}
        selectedBackground={blockSettings.accordion_selected_background}
        selectedTextColor={blockSettings.accordion_selected_text_color}
        useLineBreak={item.settings?.use_line_break}
        defaultOpen={item.settings?.open_by_default || (!hasDefaultOpenItem && index === 0)}
        allowMultipleOpen={blockSettings.allow_multiple_open}
      >
        {(item.settings?.video || item.settings?.image) && (
          <div className='accordion__content-media'>
            <Media
              videoItem={item.settings?.video}
              imageItem={item.settings?.image}
              index={index}
            />
          </div>
        )}
        {item.settings?.content && (
          <div className='accordion__content-text'>
            <Paragraph
              text={item.settings.content}
              type={blockSettings.content_font || 'b2'}
            />
          </div>
        )}
        {item.settings?.button_text && item.settings?.button_url && (
          <div className='accordion__content-button mt-4'>
            <Button
              label={item.settings.button_text}
              link={item.settings.button_url}
              style={item.settings.button_style || 'primary'}
            />
          </div>
        )}
      </Collapsible>
    );
  };

  return (
    <div
      className={`accordion-block w-full ${
        blockSettings.accordion_style ? `accordion-${blockSettings.accordion_style}` : ''
      }`}
    >
      {accordionItems.map((item, index) => renderAccordionItem(item, index))}
    </div>
  );
}

AccordionItemComponent.displayName = 'theme__block-accordion-item';
export function AccordionItemComponent({ ...props }) {
  const Collapsible = lazyComponents['Collapsible'];
  const Paragraph = lazyComponents['Paragraph'];
  const Media = lazyComponents['Media'];
  const Button = lazyComponents['Button'];

  const blockSettings = props.settings.block_settings;

  if (!blockSettings) {
    return null;
  }

  // Get parent accordion's settings if available - this would be from a parent component
  const parentSettings = props.settings.parentSettings || {};

  // Use item's text alignment or fallback to parent's or default left
  const titleAlignment = blockSettings.text_alignment || parentSettings.accordion_text_alignment || 'left';
  const iconPosition = parentSettings.icon_position || 'right';

  // Create the accordion item using the Collapsible component
  return (
    <Collapsible
      title={blockSettings.title}
      titleClass={`${parentSettings.title_font || 'bfb'} font-medium text-${titleAlignment}`}
      contentClass={`${parentSettings.content_font || 'b2'} text-${titleAlignment}`}
      openIcon={parentSettings.accordion_icon_open || 'icon-caret-down-thin'}
      closeIcon={parentSettings.accordion_icon_close || 'icon-caret-up-thin'}
      iconPosition={iconPosition}
      rotateIcon={parentSettings.rotate_icon || false}
      className={parentSettings.vertical_spacing ? '' : 'mb-0'}
      style={parentSettings.vertical_spacing ? { marginBottom: `${parentSettings.vertical_spacing}px` } : {}}
      background={parentSettings.accordion_background || 'transparent'}
      readMore={parentSettings.show_read_more}
      selectedBackground={parentSettings.accordion_selected_background}
      selectedTextColor={parentSettings.accordion_selected_text_color}
      useLineBreak={blockSettings.use_line_break}
      defaultOpen={blockSettings.open_by_default}
      allowMultipleOpen={parentSettings.allow_multiple_open}
    >
      {(blockSettings.video || blockSettings.image) && (
        <div className='accordion__content-media'>
          <Media
            videoItem={blockSettings.video}
            imageItem={blockSettings.image}
            index={0}
          />
        </div>
      )}
      {blockSettings.content && (
        <div className='accordion__content-text'>
          <Paragraph
            text={blockSettings.content}
            type={parentSettings.content_font || 'b2'}
          />
        </div>
      )}
      {blockSettings.button_text && blockSettings.button_url && (
        <div className='accordion__content-button mt-4'>
          <Button
            label={blockSettings.button_text}
            link={blockSettings.button_url}
            style={blockSettings.button_style || 'primary'}
          />
        </div>
      )}
    </Collapsible>
  );
}

CarouselComponent.displayName = 'theme__block-carousel';
export function CarouselComponent({ ...props }) {
  const Carousel = lazyComponents['Carousel'];

  const blockId = props.settings.block_id;
  const blockSettings =
    props.settings.block_settings || props.settings.blocks.find((block) => block.id === blockId)?.settings;
  const childBlocks = props.settings.blocks || [];

  if (!blockSettings) {
    return null;
  }

  // Extract carousel settings
  const showNavigation = blockSettings.show_navigation !== false;
  const showPagination = blockSettings.show_pagination !== false;
  const autoplay = blockSettings.autoplay || false;
  const autoplaySpeed = blockSettings.autoplay_speed || 3000;
  const loop = blockSettings.loop !== false;
  const aspectRatio = blockSettings.aspect_ratio || 'auto';
  const customHeight = blockSettings.custom_height || 400;
  const slideGap = blockSettings.slide_gap || 16;
  const slidesPerView = blockSettings.slides_per_view || 1;
  const slidesPerViewMobile = blockSettings.slides_per_view_mobile || 1;

  // Extract positioning data for overlay support
  const positionType = blockSettings.position_type || 'relative';
  const positionProps =
    positionType === 'absolute'
      ? {
          isOverlay: true,
          zIndex: blockSettings.z_index || 10
        }
      : {};

  return (
    <Carousel
      slides={childBlocks}
      showNavigation={showNavigation}
      showPagination={showPagination}
      autoplay={autoplay}
      autoplaySpeed={autoplaySpeed}
      loop={loop}
      aspectRatio={aspectRatio}
      customHeight={customHeight}
      slideGap={slideGap}
      slidesPerView={slidesPerView}
      slidesPerViewMobile={slidesPerViewMobile}
      {...positionProps}
    />
  );
}

DateTimePickerComponent.displayName = 'theme__block-datetime-picker';
export function DateTimePickerComponent({ ...props }) {
  const DateTimePicker = lazyComponents['DateTimePicker'];

  const blockId = props.settings.block_id;
  const blockSettings = props.settings.block_settings;

  if (!blockSettings) {
    return null;
  }

  // Extract datetime picker settings
  const inputName = blockSettings.input_name || '';
  const inputId = blockSettings.input_id || '';
  const inputLabel = blockSettings.input_label || '';
  const inputPlaceholder = blockSettings.input_placeholder || '';
  const inputRequired = blockSettings.input_required === 'true' || blockSettings.input_required === true;
  const pickerType = blockSettings.picker_type || 'datetime'; // Can be 'date', 'time', or 'datetime'
  const minDate = blockSettings.min_date || '';
  const maxDate = blockSettings.max_date || '';
  const dateFormat = blockSettings.date_format || 'YYYY-MM-DD';
  const timeFormat = blockSettings.time_format || 'HH:mm';
  const disabledDays = blockSettings.disabled_days || []; // Array of disabled days, e.g. ['saturday', 'sunday']

  // Extract positioning data for overlay support
  const positionType = blockSettings.position_type || 'relative';
  const positionProps =
    positionType === 'absolute'
      ? {
          isOverlay: true,
          zIndex: blockSettings.z_index || 10
        }
      : {};

  return (
    <DateTimePicker
      name={inputName}
      id={inputId}
      label={inputLabel}
      placeholder={inputPlaceholder}
      required={inputRequired}
      type={pickerType}
      minDate={minDate}
      maxDate={maxDate}
      dateFormat={dateFormat}
      timeFormat={timeFormat}
      disabledDays={disabledDays}
      {...positionProps}
    />
  );
}

MenuListComponent.displayName = 'theme__block-menu-list';
export function MenuListComponent({ ...props }) {
  const MenuList = lazyComponents['MenuList'];
  const Icon = lazyComponents['Icon'];

  const blockId = props.settings.block_id;
  const block = props.settings?.blocks?.find((block) => block.id === blockId) || props.settings.block_settings;
  //
  const blockSettings = block.settings || props.settings.block_settings;

  // Breakpoints for responsive design
  const breakpoints = {
    mobile_max: 767,
    tablet_min: 768,
    tablet_max: 1023,
    desktop_min: 1024,
    desktop_max: 1439,
    ultrawide_min: 1440
  };

  // Use media queries to determine current breakpoint
  const isMobile = useMediaQuery(null, breakpoints.mobile_max);
  const isTablet = useMediaQuery(breakpoints.tablet_min, breakpoints.tablet_max);
  const isDesktop = useMediaQuery(breakpoints.desktop_min, breakpoints.desktop_max);
  const isUltrawide = useMediaQuery(breakpoints.ultrawide_min, null);

  // Extract menu list settings
  const menuItems = props.settings.additional || block.links || props.settings.links || [];
  const displayStyle = blockSettings.menu_display_type || 'vertical';
  const menuItemSpacing = blockSettings.menu_item_gap;
  const menuAlignment = blockSettings.menu_alignment || 'left';

  // Extract per-level hover settings
  const hoverUnderlineLevel1 = blockSettings.hover_underline_level_1 || false;
  const hoverFontWeightLevel1 = blockSettings.hover_font_weight_level_1 || false;
  const hoverColorLevel1 = blockSettings.hover_color_level_1 || 'transparent';
  const hoverUnderlineLevel2 = blockSettings.hover_underline_level_2 || false;
  const hoverFontWeightLevel2 = blockSettings.hover_font_weight_level_2 || false;
  const hoverColorLevel2 = blockSettings.hover_color_level_2 || 'transparent';
  const hoverUnderlineLevel3 = blockSettings.hover_underline_level_3 || false;
  const hoverFontWeightLevel3 = blockSettings.hover_font_weight_level_3 || false;
  const hoverColorLevel3 = blockSettings.hover_color_level_3 || 'transparent';

  // Extract flex grow last column setting
  const flexGrowLastColumn = blockSettings.flex_grow_last_column || false;

  // Extract horizontal display settings
  const horizontalItemAlignment = blockSettings.horizontal_item_alignment || 'start';
  const horizontalItemWidth = blockSettings.horizontal_item_width || 'auto';
  const horizontalItemFlexBasis = blockSettings.horizontal_item_flex_basis || 200;
  const horizontalItemCustomWidth = blockSettings.horizontal_item_custom_width || 200;
  const horizontalJustifyContent = blockSettings.horizontal_justify_content || 'start';

  // Extract text styling settings for each level (single values only)
  const textStyleLevel1 = blockSettings.text_style_level_1 || 'b1';
  const textStyleLevel2 = blockSettings.text_style_level_2 || 'b2';
  const textStyleLevel3 = blockSettings.text_style_level_3 || 'b3';

  // Function to get responsive text style (following TextComponent pattern)
  const getResponsiveTextStyle = (baseStyle, styleMobile, styleTablet, styleUltrawide, enableResponsiveStyles) => {
    if (!enableResponsiveStyles) {
      return baseStyle;
    }

    // Priority order: mobile -> tablet -> desktop -> ultrawide
    // Use inheritance: if a breakpoint style is not set, inherit from the next larger breakpoint
    if (isUltrawide && styleUltrawide) {
      return styleUltrawide;
    }

    if (isDesktop) {
      return baseStyle; // Desktop uses the base style
    }

    if (isTablet && styleTablet) {
      return styleTablet;
    }

    if (isMobile && styleMobile) {
      return styleMobile;
    }

    // Fallback to base style
    return baseStyle;
  };

  // Get responsive text styles for each level
  const responsiveTextStyleLevel1 = getResponsiveTextStyle(
    textStyleLevel1,
    blockSettings.text_style_level_1_mobile,
    blockSettings.text_style_level_1_tablet,
    blockSettings.text_style_level_1_ultrawide,
    blockSettings.text_style_level_1_enable_responsive
  );

  const responsiveTextStyleLevel2 = getResponsiveTextStyle(
    textStyleLevel2,
    blockSettings.text_style_level_2_mobile,
    blockSettings.text_style_level_2_tablet,
    blockSettings.text_style_level_2_ultrawide,
    blockSettings.text_style_level_2_enable_responsive
  );

  const responsiveTextStyleLevel3 = getResponsiveTextStyle(
    textStyleLevel3,
    blockSettings.text_style_level_3_mobile,
    blockSettings.text_style_level_3_tablet,
    blockSettings.text_style_level_3_ultrawide,
    blockSettings.text_style_level_3_enable_responsive
  );

  // Extract uppercase settings for each level
  const uppercaseLevel1 = blockSettings.uppercase_level_1 || false;
  const uppercaseLevel2 = blockSettings.uppercase_level_2 || false;
  const uppercaseLevel3 = blockSettings.uppercase_level_3 || false;

  // Extract gap settings for levels 2 and 3
  const gapLevel2 = blockSettings.gap_level_2;
  const gapLevel3 = blockSettings.gap_level_3;

  // Extract menu item padding settings
  const menuItemPaddingX = blockSettings.menu_item_padding_x || 0;
  const menuItemPaddingY = blockSettings.menu_item_padding_y || 0;

  // Extract grid columns settings
  const gridColumns = blockSettings.grid_columns || 3;
  const gridColumnsMobile = blockSettings.grid_columns_mobile || 1;
  const gridColumnsTablet = blockSettings.grid_columns_tablet || 2;
  const gridColumnsUltrawide = blockSettings.grid_columns_ultrawide || 4;
  const gridColumnsEnableResponsive = blockSettings.grid_columns_enable_responsive || false;

  // Extract link padding settings for each level (simplified, no overrides)
  const linkPaddingTopLevel1 = blockSettings.link_padding_top_level_1 || 0;
  const linkPaddingBottomLevel1 = blockSettings.link_padding_bottom_level_1 || 0;
  const linkPaddingLeftLevel1 = blockSettings.link_padding_left_level_1 || 0;
  const linkPaddingRightLevel1 = blockSettings.link_padding_right_level_1 || 0;

  const linkPaddingTopLevel2 = blockSettings.link_padding_top_level_2 || 0;
  const linkPaddingBottomLevel2 = blockSettings.link_padding_bottom_level_2 || 0;
  const linkPaddingLeftLevel2 = blockSettings.link_padding_left_level_2 || 0;
  const linkPaddingRightLevel2 = blockSettings.link_padding_right_level_2 || 0;

  const linkPaddingTopLevel3 = blockSettings.link_padding_top_level_3 || 0;
  const linkPaddingBottomLevel3 = blockSettings.link_padding_bottom_level_3 || 0;
  const linkPaddingLeftLevel3 = blockSettings.link_padding_left_level_3 || 0;
  const linkPaddingRightLevel3 = blockSettings.link_padding_right_level_3 || 0;

  // Extract divider settings
  const showDividersBeforeSubLinks = blockSettings.show_dividers_before_sub_links || false;
  const showDividerBeforeLevel2 = blockSettings.show_divider_before_level_2 || false;
  const showDividerBeforeLevel3 = blockSettings.show_divider_before_level_3 || false;
  const dividerStyle = blockSettings.divider_style || 'solid';
  const dividerThickness = blockSettings.divider_thickness || 1;
  const dividerColor = blockSettings.divider_color || '#e0e0e0';
  const dividerMarginTop = blockSettings.divider_margin_top || 0;
  const dividerMarginBottom = blockSettings.divider_margin_bottom || 0;

  // Helper functions to get styles based on level
  const getTextStyleForLevel = (level) => {
    switch (level) {
      case 1:
        return responsiveTextStyleLevel1;
      case 2:
        return responsiveTextStyleLevel2;
      case 3:
        return responsiveTextStyleLevel3;
      default:
        return 'b1';
    }
  };

  const getUppercaseForLevel = (level) => {
    switch (level) {
      case 1:
        return uppercaseLevel1 ? 'uppercase' : '';
      case 2:
        return uppercaseLevel2 ? 'uppercase' : '';
      case 3:
        return uppercaseLevel3 ? 'uppercase' : '';
      default:
        return '';
    }
  };

  const getHoverEffectsForLevel = (level) => {
    let underlineClass = '';
    let fontWeightClass = '';
    let colorClass = '';

    switch (level) {
      case 1:
        underlineClass = hoverUnderlineLevel1 ? 'hover:!underline' : '';
        fontWeightClass = hoverFontWeightLevel1 ? 'hover:!font-bold' : '';
        colorClass =
          hoverColorLevel1 !== 'transparent' && hoverColorLevel1 !== 'rgba(0,0,0,0)'
            ? 'hover:!text-[var(--hover-color)]'
            : '';
        break;
      case 2:
        underlineClass = hoverUnderlineLevel2 ? 'hover:!underline' : '';
        fontWeightClass = hoverFontWeightLevel2 ? 'hover:!font-bold' : '';
        colorClass =
          hoverColorLevel2 !== 'transparent' && hoverColorLevel2 !== 'rgba(0,0,0,0)'
            ? 'hover:!text-[var(--hover-color)]'
            : '';
        break;
      case 3:
        underlineClass = hoverUnderlineLevel3 ? 'hover:!underline' : '';
        fontWeightClass = hoverFontWeightLevel3 ? 'hover:!font-bold' : '';
        colorClass =
          hoverColorLevel3 !== 'transparent' && hoverColorLevel3 !== 'rgba(0,0,0,0)'
            ? 'hover:!text-[var(--hover-color)]'
            : '';
        break;
      default:
        underlineClass = '';
        fontWeightClass = '';
        colorClass = '';
        break;
    }

    return `${underlineClass} underline-offset-2 ${fontWeightClass} ${colorClass}`.trim();
  };

  const getHoverStylesForLevel = (level) => {

    switch (level) {
      case 1:
        return hoverColorLevel1 !== 'transparent' && hoverColorLevel1 !== 'rgba(0,0,0,0)'
          ? { '--hover-color': hoverColorLevel1 }
          : {};
      case 2:
        return hoverColorLevel2 !== 'transparent' && hoverColorLevel2 !== 'rgba(0,0,0,0)'
          ? { '--hover-color': hoverColorLevel2 }
          : {};
      case 3:
        return hoverColorLevel3 !== 'transparent' && hoverColorLevel3 !== 'rgba(0,0,0,0)'
          ? { '--hover-color': hoverColorLevel3 }
          : {};
      default:
        return {};
    }
  };

  const getGapForLevel = (level) => {
    switch (level) {
      case 1:
        return menuItemSpacing || 0;
      case 2:
        return gapLevel2 || 0;
      case 3:
        return gapLevel3 || 0;
      default:
        return menuItemSpacing || 0;
    }
  };

  const getLinkPaddingStyles = (level) => {
    let paddingTop, paddingBottom, paddingLeft, paddingRight;

    switch (level) {
      case 1:
        paddingTop = linkPaddingTopLevel1;
        paddingBottom = linkPaddingBottomLevel1;
        paddingLeft = linkPaddingLeftLevel1;
        paddingRight = linkPaddingRightLevel1;
        break;
      case 2:
        paddingTop = linkPaddingTopLevel2;
        paddingBottom = linkPaddingBottomLevel2;
        paddingLeft = linkPaddingLeftLevel2;
        paddingRight = linkPaddingRightLevel2;
        break;
      case 3:
        paddingTop = linkPaddingTopLevel3;
        paddingBottom = linkPaddingBottomLevel3;
        paddingLeft = linkPaddingLeftLevel3;
        paddingRight = linkPaddingRightLevel3;
        break;
      default:
        return {};
    }

    const style = {};

    // Apply padding - always apply if value > 0
    if (paddingTop > 0) {
      style.paddingTop = `${paddingTop}px`;
    }

    if (paddingBottom > 0) {
      style.paddingBottom = `${paddingBottom}px`;
    }

    if (paddingLeft > 0) {
      style.paddingLeft = `${paddingLeft}px`;
    }

    if (paddingRight > 0) {
      style.paddingRight = `${paddingRight}px`;
    }

    return style;
  };

  const getResponsiveTextClasses = (level) => {
    return getTextStyleForLevel(level);
  };

  const getResponsiveGapStyles = (level) => {
    // Return gap style object for inline styles
    return {
      gap: `${getGapForLevel(level)}px`
    };
  };

  const getMenuItemPaddingStyles = () => {
    const style = {};

    // Apply horizontal padding if we have non-zero values
    if (menuItemPaddingX > 0) {
      style.paddingLeft = `${menuItemPaddingX}px`;
      style.paddingRight = `${menuItemPaddingX}px`;
    }

    // Apply vertical padding if we have non-zero values
    if (menuItemPaddingY > 0) {
      style.paddingTop = `${menuItemPaddingY}px`;
      style.paddingBottom = `${menuItemPaddingY}px`;
    }

    return style;
  };

  const getGridColumns = () => {
    // Use useMediaQuery to determine current grid columns based on breakpoint
    if (gridColumnsEnableResponsive) {
      if (isUltrawide) {
        return gridColumnsUltrawide;
      }
      if (isDesktop) {
        return gridColumns;
      }
      if (isTablet) {
        return gridColumnsTablet;
      }
      if (isMobile) {
        return gridColumnsMobile;
      }
      // Fallback
      return gridColumns;
    }
    return gridColumns;
  };

  // Get the current number of columns for responsive design
  const currentGridColumns = getGridColumns();

  // Generate grid template columns style for flex-grow functionality
  const getGridTemplateColumns = () => {
    if (displayStyle !== 'grid') return {};

    const currentColumns = currentGridColumns;

    // Always use standard grid columns - we'll handle flex-grow with grid-column on individual items
    return {
      gridTemplateColumns: `repeat(${currentColumns}, 1fr)`,
      display: 'grid'
    };
  };

  // Calculate grid-column span for the last item when flex_grow_last_column is enabled
  const getLastItemGridColumn = (index, totalItems) => {
    if (!flexGrowLastColumn || displayStyle !== 'grid') return {};

    const currentColumns = currentGridColumns;
    const isLastItem = index === totalItems - 1;

    if (isLastItem) {
      const itemsInLastRow = totalItems % currentColumns;

      if (itemsInLastRow === 0) {
        // Last row is full, no need to span extra columns
        return {};
      } else {
        // Last row is not full, make the last item span to the end of the row
        // Calculate starting column position (1-indexed)
        const startColumn = itemsInLastRow;
        const endColumn = currentColumns + 1; // Grid lines are 1-indexed, so end is columns + 1

        return {
          gridColumn: `${startColumn} / ${endColumn}`
        };
      }
    }

    return {};
  };

  // New accordion settings
  const menuDisplayType = blockSettings.menu_display_type || 'vertical';
  const enableAccordionMode = menuDisplayType === 'accordion';

  // Get the appropriate display level setting based on menu type
  const displayLevel = enableAccordionMode
    ? blockSettings.menu_display_level_accordion || '3'
    : blockSettings.menu_display_level || '2';

  // Determine which levels to show based on the setting
  let showLevel1, showLevel2, showLevel3;

  if (enableAccordionMode) {
    // Accordion mode: supports "2", "2_3", "3"
    switch (displayLevel) {
      case '2':
        showLevel1 = true;
        showLevel2 = true;
        showLevel3 = false;
        break;
      case '2_3':
        showLevel1 = false;
        showLevel2 = true;
        showLevel3 = true;
        break;
      case '3':
      default:
        showLevel1 = true;
        showLevel2 = true;
        showLevel3 = true;
        break;
    }
  } else {
    // Non-accordion mode: supports "1", "2_only", "3_only", "2", "2_3"
    switch (displayLevel) {
      case '1':
        showLevel1 = true;
        showLevel2 = false;
        showLevel3 = false;
        break;
      case '2_only':
        showLevel1 = false;
        showLevel2 = true;
        showLevel3 = false;
        break;
      case '3_only':
        showLevel1 = false;
        showLevel2 = false;
        showLevel3 = true;
        break;
      case '2':
        showLevel1 = true;
        showLevel2 = true;
        showLevel3 = false;
        break;
      case '2_3':
        showLevel1 = false;
        showLevel2 = true;
        showLevel3 = true;
        break;
      default:
        showLevel1 = true;
        showLevel2 = true;
        showLevel3 = false;
        break;
    }
  }

  // Accordion expand/collapse icon settings
  const accordionExpandIconSource = blockSettings.accordion_expand_icon_source || 'text';
  const accordionExpandIconName = blockSettings.accordion_expand_icon_name || 'chevron-down';
  const accordionExpandCustomIcon = blockSettings.accordion_expand_custom_icon;
  const accordionExpandIconText = blockSettings.accordion_expand_icon_text || '⮟';

  const accordionCollapseIconSource = blockSettings.accordion_collapse_icon_source || 'text';
  const accordionCollapseIconName = blockSettings.accordion_collapse_icon_name || 'chevron-up';
  const accordionCollapseCustomIcon = blockSettings.accordion_collapse_custom_icon;
  const accordionCollapseIconText = blockSettings.accordion_collapse_icon_text || '⮝';

  const accordionExpandCollapseIconSize = blockSettings.accordion_expand_collapse_icon_size || 16;
  const expandFirstItem = blockSettings.accordion_expand_first || false;

  // Extract disable level 2 accordion setting
  const disableLevel2Accordion = blockSettings.disable_level_2_accordion || false;

  // Extract interaction event settings
  const enableInteractionEvents = blockSettings.enable_interaction_events || false;
  const interactionEventIds =
    (blockSettings.interaction_event_ids || '')
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0) || [];
  const interactionType = blockSettings.interaction_type || 'hover';

  // Helper function to render accordion icons
  const renderAccordionIcon = (isExpanded) => {
    const iconSource = isExpanded ? accordionCollapseIconSource : accordionExpandIconSource;
    const iconName = isExpanded ? accordionCollapseIconName : accordionExpandIconName;
    const customIcon = isExpanded ? accordionCollapseCustomIcon : accordionExpandCustomIcon;
    const iconText = isExpanded ? accordionCollapseIconText : accordionExpandIconText;

    switch (iconSource) {
      case 'icon_name':
        return (
          <Icon
            settings={blockSettings}
            iconSource={iconSource}
            iconName={iconName}
            customIcon={customIcon}
            iconSize={accordionExpandCollapseIconSize}
          />
        );
      case 'custom_image':
        return customIcon ? (
          <img
            src={customIcon}
            alt={isExpanded ? 'Collapse' : 'Expand'}
            className='accordion-icon'
            style={{ width: `${accordionExpandCollapseIconSize}px`, height: `${accordionExpandCollapseIconSize}px` }}
          />
        ) : (
          <span className='text-sm'>{iconText}</span>
        );
      case 'text':
      default:
        return <span className='text-sm'>{iconText}</span>;
    }
  };

  // Helper function to render dividers before sub links
  const renderDivider = (level) => {
    // Check if dividers are enabled globally and for this specific level
    if (!showDividersBeforeSubLinks) return null;

    const shouldShowDivider = (level === 2 && showDividerBeforeLevel2) || (level === 3 && showDividerBeforeLevel3);

    if (!shouldShowDivider) return null;

    const dividerStyles = {
      borderTop: `${dividerThickness}px ${dividerStyle} ${dividerColor}`,
      marginTop: `${dividerMarginTop}px`,
      marginBottom: `${dividerMarginBottom}px`,
      width: '100%'
    };

    return (
      <div
        className='menu-divider'
        style={dividerStyles}
        aria-hidden='true'
      />
    );
  };

  // Function to create a reusable AccordionComponent for menu items
  const AccordionComponent = ({ title, url, target, children, isOpen, onToggle, level = 1, icon, itemKey }) => {
    const hasChildren = children && children.length > 0;
    const levelTextStyles = getResponsiveTextClasses(level);
    const linkPaddingStyle = getLinkPaddingStyles(level);
    const menuItemPaddingStyle = getMenuItemPaddingStyles();
    // For accordion children container, we need gap for level + 1 (the children's level)
    const childrenGapStyle = hasChildren ? getResponsiveGapStyles(level + 1) : {};

    // Check if this is level 2 and accordion is disabled
    const isLevel2WithDisabledAccordion = level === 2 && disableLevel2Accordion;

    const handleLinkClick = (e) => {
      e.stopPropagation();

      // Check if URL is valid (not empty, not '#', not null/undefined)
      const hasValidUrl = url && url !== '#' && url.trim() !== '';

      if (hasValidUrl) {
        // Handle link navigation for valid URLs
        if (target === '_blank') {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = url;
        }
        // Dispatch interaction events for navigation
        if (interactionType === 'click' || interactionType === 'both') {
          dispatchInteractionEvent('click', 'navigate', { title, url, level });
        }
      } else if (hasChildren && !isLevel2WithDisabledAccordion) {
        // If no valid URL but has children, toggle accordion instead
        onToggle();
        // Dispatch interaction events for toggle
        if (interactionType === 'click' || interactionType === 'both') {
          dispatchInteractionEvent('click', 'toggle', { title, url, level });
        }
      }
    };

    const handleToggleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    };

    const handleMouseEnter = (e) => {
      handleHover(e, { title, url, level }, true);
    };

    const handleMouseLeave = (e) => {
      handleHover(e, { title, url, level }, false);
    };

    // If level 2 accordion is disabled, render as title with children below
    if (isLevel2WithDisabledAccordion) {
      return (
        <div className={`menu-level-2-static`}>
          <div
            className={`menu-level-2-title ${levelTextStyles} ${getUppercaseForLevel(
              level
            )} cursor-pointer ${getHoverEffectsForLevel(level)}`}
            style={{ ...linkPaddingStyle, ...menuItemPaddingStyle, ...getHoverStylesForLevel(level) }}
            onClick={handleLinkClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {title}
          </div>

          {/* Level 3 children */}
          {hasChildren && (
            <div
              className='menu-level-3-submenu flex flex-col'
              style={{
                ...childrenGapStyle
              }}
            >
              {children}
            </div>
          )}
        </div>
      );
    }

    // Regular accordion behavior for other levels
    return (
      <div className={`menu-accordion-item`}>
        <div
          className={`menu-accordion-header flex items-center justify-between cursor-pointer transition-colors duration-200 ${levelTextStyles}`}
          style={{ ...linkPaddingStyle, ...menuItemPaddingStyle }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Title - clickable for navigation or toggle */}
          <div
            className={`menu-title flex-1 text-left ${getUppercaseForLevel(level)} ${getHoverEffectsForLevel(level)}`}
            style={getHoverStylesForLevel(level)}
            onClick={handleLinkClick}
          >
            {title}
          </div>

          {/* Accordion toggle icon - only show if there are children and not level 2 with disabled accordion */}
          {hasChildren && !isLevel2WithDisabledAccordion && (
            <button
              className='accordion-toggle transition-transform duration-200'
              onClick={handleToggleClick}
            >
              {renderAccordionIcon(isOpen)}
            </button>
          )}
        </div>

        {/* Children content */}
        {hasChildren && (
          <div
            className={`menu-accordion-content overflow-hidden transition-all duration-300 ease-in-out ${
              isOpen ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              maxHeight: isOpen ? '1000px' : '0'
            }}
          >
            <div
              className='flex flex-col'
              style={childrenGapStyle}
            >
              {children}
            </div>
          </div>
        )}
      </div>
    );
  };

  // State management for accordion open/close
  const [openItems, setOpenItems] = React.useState(() => {
    const initialSet = new Set();
    // If expandFirstItem is enabled and we have menu items, expand the first one
    if (expandFirstItem && menuItems && menuItems.length > 0) {
      // Generate the correct key for the first item based on display level
      let firstItemKey = '';

      if (enableAccordionMode) {
        switch (displayLevel) {
          case '2_3':
            // First level 2 item would be under first level 1 item
            if (menuItems[0]?.links?.length > 0) {
              firstItemKey = '-1-2-0'; // First level 2 item
            }
            break;
          case '2':
          case '3':
          default:
            firstItemKey = '-1-1-0'; // First level 1 item
            break;
        }
      } else {
        // Non-accordion mode
        switch (displayLevel) {
          case '2_only':
            // First level 2 item would be under first level 1 item
            if (menuItems[0]?.links?.length > 0) {
              firstItemKey = '-1-2-0'; // First level 2 item
            }
            break;
          case '3_only':
            // First level 3 item would be under first level 1 and 2 items
            if (menuItems[0]?.links?.[0]?.links?.length > 0) {
              firstItemKey = '-1-3-0'; // First level 3 item
            }
            break;
          default:
            firstItemKey = '-1-1-0'; // First level 1 item
            break;
        }
      }

      if (firstItemKey) {
        initialSet.add(firstItemKey);
      }
    }
    return initialSet;
  });

  const toggleItem = (itemKey) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  // Function to render menu items recursively for accordion mode
  const renderAccordionMenu = (items, level = 1, parentKey = '', originalLevel = 1) => {
    if (!items || items.length === 0) return null;

    // Check if we should show this level
    if (level === 1 && !showLevel1) {
      // For level-only modes, we need to drill down to find the target level
      if (displayLevel === '2_3') {
        const flattenedItems = [];
        items.forEach((item, index) => {
          if (item.links && item.links.length > 0) {
            const childItems = renderAccordionMenu(
              item.links,
              level + 1,
              `${parentKey}-${originalLevel}-${index}`,
              originalLevel
            );
            if (childItems) {
              flattenedItems.push(...(Array.isArray(childItems) ? childItems : [childItems]));
            }
          }
        });
        return flattenedItems.length > 0 ? flattenedItems : null;
      }
      return null;
    }
    if (level === 2 && !showLevel2) return null;
    if (level === 3 && !showLevel3) return null;

    return items.map((item, index) => {
      const itemKey = `${parentKey}-${level}-${index}`;
      const isOpen = openItems.has(itemKey);
      const hasChildren = item.links && item.links.length > 0;

      // For level 2 with disabled accordion, force open state
      const shouldForceOpen = level === 2 && disableLevel2Accordion;

      return (
        <React.Fragment key={itemKey}>
          {/* Render divider before the first menu item if applicable - only for levels 2 and 3 and only for first item */}
          {level > 1 && index === 0 && renderDivider(level)}

          <AccordionComponent
            title={item.title}
            url={item.url}
            target={item.target}
            isOpen={shouldForceOpen || isOpen}
            onToggle={() => !shouldForceOpen && toggleItem(itemKey)}
            level={level}
            icon={item.icon}
            itemKey={itemKey}
          >
            {hasChildren && ((level === 1 && showLevel2) || (level === 2 && showLevel3))
              ? renderAccordionMenu(item.links, level + 1, itemKey, originalLevel)
              : null}
          </AccordionComponent>
        </React.Fragment>
      );
    });
  };

  // Container classes based on valid settings
  const containerClasses = [
    displayStyle === 'vertical'
      ? 'flex flex-col'
      : displayStyle === 'horizontal'
      ? 'flex flex-row'
      : displayStyle === 'grid'
      ? 'grid'
      : 'flex flex-col',
    displayStyle === 'horizontal' ? `justify-${horizontalJustifyContent}` : `justify-${menuAlignment}`,
    displayStyle === 'horizontal' ? `items-${horizontalItemAlignment}` : `items-${menuAlignment}`,
    // Apply flex-grow for horizontal layout if enabled
    displayStyle === 'horizontal' && flexGrowLastColumn ? '[&>*:last-child]:!flex-grow' : ''
    // Apply Menu Item Gap (level 1 gap) to the main container - moved to inline styles
  ]
    .filter(Boolean)
    .join(' ');

  const containerStyle = {
    ...getResponsiveGapStyles(1),
    // Apply grid template columns for grid layout with flex-grow support
    ...(displayStyle === 'grid' ? getGridTemplateColumns() : {})
  };

  // Helper function to determine if an item is the last in its row for horizontal line extension
  const getHorizontalLineClasses = (index, totalItems, level) => {
    // Horizontal line extension functionality has been removed
    return '';
  };

  // Helper function to get horizontal display styles (for level 1 containers only)
  const getHorizontalItemStyles = (level = 1, itemIndex = null, totalItems = null) => {
    if (level !== 1) {
      return {};
    }

    const style = {};

    // For grid layout, add grid-column logic for flex-grow last column
    if (displayStyle === 'grid' && itemIndex !== null && totalItems !== null) {
      Object.assign(style, getLastItemGridColumn(itemIndex, totalItems));
    }

    // For horizontal layout, apply width/flex styles
    if (displayStyle === 'horizontal') {
      if (horizontalItemWidth === 'flex') {
        style.flexBasis = `${horizontalItemFlexBasis}px`;
        style.flexGrow = 0;
        style.flexShrink = 0;
      } else if (horizontalItemWidth === 'equal') {
        style.flex = '1 1 0%';
      } else if (horizontalItemWidth === 'custom') {
        style.width = `${horizontalItemCustomWidth}px`;
        style.flexGrow = 0;
        style.flexShrink = 0;
      } else {
        // auto - fit content
        style.width = 'auto';
      }
    }

    return style;
  };

  // Function to dispatch interaction events
  const dispatchInteractionEvent = (eventType, action, link) => {
    if (enableInteractionEvents && interactionEventIds.length > 0 && typeof window !== 'undefined') {
      try {
        interactionEventIds.forEach((eventId) => {
          if (!eventId) return; // Skip empty IDs

          window.dispatchEvent(
            new CustomEvent(`interaction:${eventType}`, {
              detail: {
                eventId,
                eventType: eventType,
                action: action,
                linkData: link
              }
            })
          );
        });
        // console.log('Dispatched menu interaction event:', eventType, { eventId: interactionEventId, action, link: link.title });
      } catch (error) {
        console.error('Error dispatching menu interaction event:', error);
      }
    }
  };

  // Function to handle hover events
  const handleHover = (event, link, isEnter) => {
    // Dispatch interaction events
    if (interactionType === 'hover' || interactionType === 'both') {
      dispatchInteractionEvent('hover', isEnter ? 'enter' : 'leave', link);
    }
  };

  // Function to filter menu items by level for regular menu display
  const filterMenuByLevel = (items) => {
    if (!items || items.length === 0) return [];

    // Helper function to recursively collect items at specific levels
    const collectItemsAtLevel = (menuItems, targetLevel, currentLevel = 1) => {
      let result = [];

      for (const item of menuItems) {
        if (currentLevel === targetLevel) {
          result.push({ ...item, links: [] }); // Include item without children
        } else if (currentLevel < targetLevel && item.links) {
          result.push(...collectItemsAtLevel(item.links, targetLevel, currentLevel + 1));
        }
      }

      return result;
    };

    // Helper function to build hierarchical structure with specific levels
    const buildHierarchy = (menuItems, includeLevels, currentLevel = 1) => {
      let result = [];

      for (const item of menuItems) {
        if (includeLevels.includes(currentLevel)) {
          const newItem = { ...item };

          // Only include children if the next level should also be included
          if (item.links && includeLevels.includes(currentLevel + 1)) {
            newItem.links = buildHierarchy(item.links, includeLevels, currentLevel + 1);
          } else {
            newItem.links = [];
          }

          result.push(newItem);
        } else if (item.links && currentLevel < Math.max(...includeLevels)) {
          // If current level is not included but we need to go deeper, recurse
          result.push(...buildHierarchy(item.links, includeLevels, currentLevel + 1));
        }
      }

      return result;
    };

    // Apply filtering based on display level setting
    switch (displayLevel) {
      case '1':
        // Show only level 1
        return items.map((item) => ({ ...item, links: [] }));

      case '2_only':
        // Show only level 2 items (flatten to show as top-level)
        return collectItemsAtLevel(items, 2);

      case '3_only':
        // Show only level 3 items (flatten to show as top-level)
        return collectItemsAtLevel(items, 3);

      case '2':
        // Show levels 1 & 2 in hierarchy
        return buildHierarchy(items, [1, 2]);

      case '2_3':
        // Show levels 2 & 3 in hierarchy (start from level 2, include level 3)
        return buildHierarchy(items, [2, 3]);

      default:
        // Default to showing levels 1 & 2
        return buildHierarchy(items, [1, 2]);
    }
  };

  // Function to handle click events
  const handleClick = (event, link) => {
    // Dispatch interaction events for click
    if (interactionType === 'click' || interactionType === 'both') {
      dispatchInteractionEvent('click', 'trigger', link);
    }
  };

  // Function to render menu items for vertical/horizontal lists
  const renderListMenu = (items) => {
    // Recursive function to render nested menu structure
    const renderMenuLevel = (menuItems, level = 1) => {
      const levelTextStyles = getResponsiveTextClasses(level);
      const linkPaddingStyle = getLinkPaddingStyles(level);
      const menuItemPaddingStyle = getMenuItemPaddingStyles();
      // Get gap style for the current level (this will be applied to the container holding these items)
      const currentLevelGapStyle = getResponsiveGapStyles(level);

      return menuItems.map((link, idx) => {
        const hasChildren = link.links && link.links.length > 0;
        const horizontalLineClasses = getHorizontalLineClasses(idx, menuItems.length, level);

        const linkElement = (
          <a
            key={link.handle || link.title || idx}
            title={link.title}
            href={link.url === '#' ? undefined : link.url}
            className={`inline-block ${levelTextStyles} ${getUppercaseForLevel(
              level
            )} ${horizontalLineClasses} ${getHoverEffectsForLevel(level)}`}
            style={{ ...linkPaddingStyle, ...menuItemPaddingStyle, ...getHoverStylesForLevel(level) }}
            target={link.target || '_self'}
            rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
            onMouseEnter={(e) => handleHover(e, link, true)}
            onMouseLeave={(e) => handleHover(e, link, false)}
            onClick={(e) => handleClick(e, link)}
            data-menu-item={link.handle || link.title || idx}
            data-menu-level={level}
          >
            <span className='align-middle'>{link.title}</span>
          </a>
        );

        // Create wrapper for each menu item that includes dividers
        const menuItemWithDivider = (
          <React.Fragment key={`item-${link.handle || link.title || idx}-${level}`}>
            {/* Render divider before menu item if it's first item of level 2 or 3 */}
            {idx === 0 && renderDivider(level)}
            {linkElement}
          </React.Fragment>
        );

        // If this item has children, render them in a nested structure
        if (hasChildren) {
          // Get gap style for the next level (level + 1) for the submenu container
          const nextLevelGapStyle = getResponsiveGapStyles(level + 1);
          // Get horizontal styles for level 1 containers only
          const horizontalContainerStyle = getHorizontalItemStyles(level, idx, menuItems.length);

          return (
            <div
              key={`${link.handle || link.title || idx}-container`}
              className='menu-item-container'
              style={horizontalContainerStyle}
            >
              {menuItemWithDivider}
              <div
                className={`menu-submenu level-${level + 1} flex flex-col`}
                style={nextLevelGapStyle}
              >
                {renderMenuLevel(link.links, level + 1)}
              </div>
            </div>
          );
        }

        // For level 1 items without children, apply horizontal styles directly
        const isLevel1 = level === 1;
        const horizontalContainerStyle = isLevel1 ? getHorizontalItemStyles(level, idx, menuItems.length) : {};

        return (
          <div
            key={`${link.handle || link.title || idx}-wrapper`}
            style={horizontalContainerStyle}
          >
            {menuItemWithDivider}
          </div>
        );
      });
    };

    return renderMenuLevel(items);
  };

  // Get filtered menu items for regular display
  const filteredMenuItems = enableAccordionMode ? menuItems : filterMenuByLevel(menuItems);

  // Render accordion mode if enabled, otherwise regular menu
  if (enableAccordionMode) {
    const accordionContainerClasses = [
      displayStyle === 'grid' ? 'grid' : 'flex flex-col',
      // Apply flex-grow for non-grid layout if enabled
      displayStyle !== 'grid' && flexGrowLastColumn ? '[&>*:last-child]:!flex-grow' : ''
      // Apply Menu Item Gap (level 1 gap) to the main accordion container - moved to inline styles
    ]
      .filter(Boolean)
      .join(' ');

    const accordionContainerStyle = {
      ...getResponsiveGapStyles(1),
      // Apply grid template columns for grid layout with flex-grow support
      ...(displayStyle === 'grid' ? getGridTemplateColumns() : {})
    };

    return (
      <nav
        className={`menu-accordion ${accordionContainerClasses}`}
        style={accordionContainerStyle}
      >
        {renderAccordionMenu(menuItems)}
      </nav>
    );
  }

  // Regular menu rendering (non-accordion)
  return (
    <nav
      className={containerClasses}
      style={containerStyle}
    >
      {renderListMenu(filteredMenuItems)}
    </nav>
  );
}

LiquidCodeComponent.displayName = 'theme__block-liquid-code';
export function LiquidCodeComponent({ ...props }) {
  // This component simply renders a container since the actual Liquid code
  // is processed server-side and the result is what gets sent to the client

  return (
    <div className='liquid-code-block w-full'>
      {/* The processed Liquid code output would be rendered here */}
      <div dangerouslySetInnerHTML={{ __html: props.settings.html_content || '' }} />
    </div>
  );
}

ProductComponent.displayName = 'theme__block-product';
export function ProductComponent({ ...props }) {
  const Product = lazyComponents['Product'];

  const blockId = props.settings.block_id;
  const blockSettings = props.settings.block_settings;
  const productData = props.settings.product;

  if (!blockSettings || !productData) {
    return null;
  }

  // Extract product display settings
  const displayMode = blockSettings.display_mode || 'card';
  const showTitle = blockSettings.show_title !== false;
  const showPrice = blockSettings.show_price !== false;
  const showVariants = blockSettings.show_variants !== false;
  const showDescription = blockSettings.show_description || false;
  const showAddToCart = blockSettings.show_add_to_cart !== false;
  const showQuantity = blockSettings.show_quantity !== false;
  const imageAspectRatio = blockSettings.image_aspect_ratio || '4:3';

  // Extract positioning data for overlay support
  const positionType = blockSettings.position_type || 'relative';
  const positionProps =
    positionType === 'absolute'
      ? {
          isOverlay: true,
          zIndex: blockSettings.z_index || 10
        }
      : {};

  return (
    <Product
      product={productData}
      displayMode={displayMode}
      showTitle={showTitle}
      showPrice={showPrice}
      showVariants={showVariants}
      showDescription={showDescription}
      showAddToCart={showAddToCart}
      showQuantity={showQuantity}
      imageAspectRatio={imageAspectRatio}
      {...positionProps}
    />
  );
}

ProductTileComponent.displayName = 'theme__block-product-tile';
export function ProductTileComponent({ ...props }) {
  const ProductTile = lazyComponents['ProductTile'];

  const blockSettings = props.settings.block_settings;
  const globals = props.settings.globals;
  const productData = props.settings.additional;
  const parentBlockSettings = props.settings.parent_block_settings || {};

  if (!blockSettings || !productData) {
    return null;
  }

  // Extract product display settings
  const showPrice = blockSettings.show_price || parentBlockSettings.show_price || true;
  const showTags = blockSettings.show_tags || parentBlockSettings.show_tags || true;
  const showQuickAdd = blockSettings.show_quickadd || parentBlockSettings.show_quickadd || true;
  const enableWishlist =
    blockSettings.enable_wishlist || parentBlockSettings.enable_wishlist || globals.enable_wishlist;
  const tileType = blockSettings.tile_type || parentBlockSettings.tile_type || 'default';

  // console.log('ProductTileComponent props 2:', blockSettings);
  return (
    <ProductTile
      product={productData}
      settings={blockSettings}
      globals={globals}
      showPrice={showPrice}
      showTags={showTags}
      showQuickAdd={showQuickAdd}
      enableWishlist={enableWishlist}
      type={tileType}
    />
  );
}

ScrollableComponent.displayName = 'theme__block-scrollable';
export function ScrollableComponent({ ...props }) {
  const [containerRef, setContainerRef] = React.useState(null);
  const [isAtStart, setIsAtStart] = React.useState(true);
  const [isAtEnd, setIsAtEnd] = React.useState(false);

  const blockId = props.settings.block_id;
  const blockSettings = props.settings.block_settings || {};

  if (!blockSettings) {
    return null;
  }

  // Extract scrollable settings
  const scrollDirection = blockSettings.scroll_direction || 'vertical';
  const height = parseInt(blockSettings.height || 300, 10);
  const maxHeight = parseInt(blockSettings.max_height || 500, 10);
  const itemsGap = parseInt(blockSettings.items_gap || 16, 10);
  const hideScrollbar = blockSettings.hide_scrollbar || false;
  const showScrollIndicators = blockSettings.show_scroll_indicators !== false;
  const snapScroll = blockSettings.snap_scroll || false;
  const scrollSpeed = parseInt(blockSettings.scroll_speed || 5, 10) * 100; // Convert speed setting to pixels
  const visibleItems = parseInt(blockSettings.visible_items || 0, 10);
  const itemMinWidth = parseInt(blockSettings.item_min_width || 260, 10);

  // Create styles based on scroll direction
  const containerStyles = {
    height: scrollDirection === 'horizontal' ? `${height}px` : scrollDirection === 'vertical' ? `${height}px` : 'auto',
    maxHeight: maxHeight > 0 ? `${maxHeight}px` : undefined
  };

  // Create classes for the container
  const containerClasses = ['scrollable-component', 'w-full', 'relative'].filter(Boolean).join(' ');

  // Create classes for the scrollable area
  const scrollableClasses = [
    'scrollable-area',
    scrollDirection === 'horizontal' ? 'overflow-x-auto overflow-y-hidden' : '',
    scrollDirection === 'vertical' ? 'overflow-y-auto overflow-x-hidden' : '',
    scrollDirection === 'both' ? 'overflow-auto' : '',
    hideScrollbar ? 'scrollbar-hide' : '',
    'w-full h-full'
  ]
    .filter(Boolean)
    .join(' ');

  // Create styles for the inner container
  const innerContainerStyles = {
    display: scrollDirection === 'horizontal' ? 'flex' : 'block',
    flexDirection: scrollDirection === 'horizontal' ? 'row' : undefined,
    gap: `${itemsGap}px`,
    width: scrollDirection === 'horizontal' ? 'max-content' : '100%'
  };

  // Create classes for the inner container
  const innerContainerClasses = [
    'scrollable-inner-container',
    scrollDirection === 'horizontal' && snapScroll ? 'snap-x snap-mandatory' : ''
  ]
    .filter(Boolean)
    .join(' ');

  // Create styles for the items wrapper
  const itemsWrapperStyles = {
    display: 'grid',
    gap: `${itemsGap}px`,
    gridAutoFlow: scrollDirection === 'horizontal' ? 'column' : 'row'
  };

  // Add grid template columns based on visible items setting
  if (scrollDirection === 'horizontal' && visibleItems > 0) {
    itemsWrapperStyles.gridTemplateColumns = `repeat(${visibleItems}, minmax(0, 1fr))`;
    itemsWrapperStyles.gridAutoColumns = `${itemMinWidth}px`;
  } else if (scrollDirection === 'vertical') {
    itemsWrapperStyles.gridTemplateColumns = '1fr';
  }

  // Update scroll position indicators on scroll
  React.useEffect(() => {
    if (!containerRef) return;

    const checkScrollPosition = () => {
      if (scrollDirection === 'horizontal') {
        setIsAtStart(containerRef.scrollLeft <= 0);
        setIsAtEnd(containerRef.scrollLeft + containerRef.clientWidth >= containerRef.scrollWidth - 5);
      } else if (scrollDirection === 'vertical') {
        setIsAtStart(containerRef.scrollTop <= 0);
        setIsAtEnd(containerRef.scrollTop + containerRef.clientHeight >= containerRef.scrollHeight - 5);
      }
    };

    containerRef.addEventListener('scroll', checkScrollPosition);
    checkScrollPosition(); // Initial check

    // Re-check when window resizes or content changes
    window.addEventListener('resize', checkScrollPosition);
    const observer = new MutationObserver(checkScrollPosition);
    observer.observe(containerRef, { childList: true, subtree: true });

    return () => {
      containerRef.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
      observer.disconnect();
    };
  }, [containerRef, scrollDirection]);

  // Handle scroll controls
  const scroll = (direction) => {
    if (!containerRef) return;

    const scrollAmount = parseInt(blockSettings.scroll_amount || 300, 10);
    const scrollOptions = { behavior: 'smooth' };

    switch (direction) {
      case 'left':
        scrollOptions.left = -scrollAmount;
        break;
      case 'right':
        scrollOptions.left = scrollAmount;
        break;
      case 'up':
        scrollOptions.top = -scrollAmount;
        break;
      case 'down':
        scrollOptions.top = scrollAmount;
        break;
    }

    containerRef.scrollBy(scrollOptions);
  };

  // Extract positioning data for overlay support
  const positionType = blockSettings.position_type || 'relative';
  const positionProps =
    positionType === 'absolute'
      ? {
          position: 'absolute',
          top: `${blockSettings.position_top || 0}%`,
          left: `${blockSettings.position_left || 0}%`,
          right: blockSettings.position_right ? `${blockSettings.position_right}%` : 'auto',
          bottom: blockSettings.position_bottom ? `${blockSettings.position_bottom}%` : 'auto',
          zIndex: blockSettings.z_index || 10,
          transform: `${blockSettings.position_left && !blockSettings.position_right ? 'translateX(-50%)' : ''} ${
            blockSettings.position_top && !blockSettings.position_bottom ? 'translateY(-50%)' : ''
          }`.trim()
        }
      : {};

  // Animation settings
  const enableAnimations = blockSettings.enable_animations || false;
  const animationType = blockSettings.animation_type || 'slide-fade-up';
  const animationDuration = blockSettings.animation_duration || 800;
  const animationDelay = blockSettings.animation_delay || 0;
  const animationTrigger = blockSettings.animation_trigger || 'onscroll';

  // Use the animation hook
  const animation = useAnimation({
    enableAnimations,
    animationType,
    animationDuration,
    animationDelay,
    animationTrigger
  });

  return (
    <div
      ref={animation.ref}
      style={{ ...positionProps, ...animation.style }}
      className={containerClasses}
    >
      {showScrollIndicators && scrollDirection === 'horizontal' && (
        <div className='scroll-controls flex justify-between absolute top-1/2 -translate-y-1/2 left-0 right-0 z-10 px-2 pointer-events-none'>
          <button
            onClick={() => scroll('left')}
            className={`scroll-left p-2 rounded-full bg-gray-200 hover:bg-gray-300 pointer-events-auto transition-opacity ${
              isAtStart ? 'opacity-30 cursor-not-allowed' : 'opacity-80'
            }`}
            disabled={isAtStart}
          >
            <span
              className='icon-arrow-left'
              aria-hidden='true'
            ></span>
            <span className='sr-only'>Scroll left</span>
          </button>
          <button
            onClick={() => scroll('right')}
            className={`scroll-right p-2 rounded-full bg-gray-200 hover:bg-gray-300 pointer-events-auto transition-opacity ${
              isAtEnd ? 'opacity-30 cursor-not-allowed' : 'opacity-80'
            }`}
            disabled={isAtEnd}
          >
            <span
              className='icon-arrow-right'
              aria-hidden='true'
            ></span>
            <span className='sr-only'>Scroll right</span>
          </button>
        </div>
      )}

      <div className='scrollable-wrapper relative w-full h-full'>
        {showScrollIndicators && scrollDirection === 'vertical' && (
          <button
            onClick={() => scroll('up')}
            className={`scroll-up absolute top-0 left-1/2 -translate-x-1/2 p-2 rounded-full bg-gray-200 hover:bg-gray-300 z-10 transition-opacity ${
              isAtStart ? 'opacity-30 cursor-not-allowed' : 'opacity-80'
            }`}
            disabled={isAtStart}
          >
            <span
              className='icon-arrow-up'
              aria-hidden='true'
            ></span>
            <span className='sr-only'>Scroll up</span>
          </button>
        )}

        <div
          ref={setContainerRef}
          className={scrollableClasses}
          style={containerStyles}
        >
          <div
            className={innerContainerClasses}
            style={innerContainerStyles}
          >
            <div
              className='scrollable-items-wrapper'
              style={itemsWrapperStyles}
            >
              {/* The content will be rendered by the Liquid template */}
              {props.settings.content ? (
                <div dangerouslySetInnerHTML={{ __html: props.settings.content }} />
              ) : props.children ? (
                props.children
              ) : (
                <div className='scrollable-placeholder text-center p-4 text-gray-500'></div>
              )}
            </div>
          </div>
        </div>

        {showScrollIndicators && scrollDirection === 'vertical' && (
          <button
            onClick={() => scroll('down')}
            className={`scroll-down absolute bottom-0 left-1/2 -translate-x-1/2 p-2 rounded-full bg-gray-200 hover:bg-gray-300 z-10 transition-opacity ${
              isAtEnd ? 'opacity-30 cursor-not-allowed' : 'opacity-80'
            }`}
            disabled={isAtEnd}
          >
            <span
              className='icon-arrow-down'
              aria-hidden='true'
            ></span>
            <span className='sr-only'>Scroll down</span>
          </button>
        )}
      </div>
    </div>
  );
}

DividerComponent.displayName = 'theme__block-divider';
export function DividerComponent({ ...props }) {
  const WaveDivider = lazyComponents['WaveDivider'];

  const blockId = props.settings.block_id;
  const blockSettings = props.settings.block_settings || {};

  if (!blockSettings) {
    return null;
  }

  // Extract the divider settings
  const waveSettings = {
    wave_height: blockSettings.wave_height || 80,
    wave_color: blockSettings.wave_color || '#ffffff',
    wave_opacity: blockSettings.wave_opacity ? blockSettings.wave_opacity / 100.0 : 1,
    wave_style: blockSettings.wave_style || 'wavy',
    flip_vertically: blockSettings.flip_vertically || false,
    flip_horizontally: blockSettings.flip_horizontally || false,
    hide_on_mobile: blockSettings.hide_on_mobile || false,
    z_index: blockSettings.z_index || 1,
    position: blockSettings.position || 'bottom',
    render_type: blockSettings.render_type || 'all',
    custom_id: blockSettings.custom_id || '',
    custom_class: blockSettings.custom_class || '',
    show_section_borders: blockSettings.show_section_borders || false,
    background_color: blockSettings.background_color || ''
  };

  return <WaveDivider settings={waveSettings} />;
}

ProductSellingPlansBlockComponent.displayName = 'theme__block-product-sellingplans';
export function ProductSellingPlansBlockComponent({ ...props }) {
  const ProductSellingGroups = lazyComponents['ProductSellingGroups'];

  const blockSettings = props.settings.block_settings;
  const productData = props.settings.additional || props.settings.product;

  if (!blockSettings || !productData) {
    return null;
  }

  // Extract selling plans settings
  const title = blockSettings.title || 'Purchase Options';
  const oneTimeLabel = blockSettings.one_time_label || 'One-time purchase';
  const subscriptionLabel = blockSettings.subscription_label || 'Subscribe & Save';
  const showSavingsBadge = blockSettings.show_savings_badge !== false;
  const savingsText = blockSettings.savings_text || 'Save {percentage}%';
  const displayLayout = blockSettings.display_layout || 'radio';
  const showDescription = blockSettings.show_description !== false;
  const showPriceComparison = blockSettings.show_price_comparison !== false;
  const benefitsText = blockSettings.benefits_text || 'Skip, pause, or cancel anytime';

  return (
    <div className='product-selling-plans-block'>
      <ProductSellingGroups
        product={productData}
        title={title}
        oneTimeLabel={oneTimeLabel}
        subscriptionLabel={subscriptionLabel}
        showSavingsBadge={showSavingsBadge}
        savingsText={savingsText}
        layout={displayLayout}
        showDescription={showDescription}
        showPriceComparison={showPriceComparison}
        benefitsText={benefitsText}
        settings={blockSettings}
      />
    </div>
  );
}

FindInStoreBlockComponent.displayName = 'theme__block-find-in-store';
export function FindInStoreBlockComponent({ ...props }) {
  const FindInStore = lazyComponents['FindInStore'];

  const blockSettings = props.settings.block_settings;

  if (!blockSettings) {
    return null;
  }

  // Extract find in store settings
  const title = blockSettings.title || 'Find in Store';
  const subtitle = blockSettings.subtitle || 'Check availability at your local store';
  const buttonText = blockSettings.button_text || 'Find Stores';
  const placeholderText = blockSettings.placeholder_text || 'Enter your location or ZIP code';
  const loadingText = blockSettings.loading_text || 'Searching stores...';
  const noResultsText = blockSettings.no_results_text || 'No stores found in your area';
  const errorText = blockSettings.error_text || 'Error finding stores. Please try again.';

  return (
    <div className='find-in-store-block'>
      <FindInStore
        title={title}
        subtitle={subtitle}
        buttonText={buttonText}
        placeholderText={placeholderText}
        loadingText={loadingText}
        noResultsText={noResultsText}
        errorText={errorText}
        settings={blockSettings}
      />
    </div>
  );
}

BackInStockBlockComponent.displayName = 'theme__block-back-in-stock';
export function BackInStockBlockComponent({ ...props }) {
  const BackInStock = lazyComponents['BackInStock'];

  const blockSettings = props.settings.block_settings;

  if (!blockSettings) {
    return null;
  }

  // Extract back in stock settings
  const title = blockSettings.title || 'Get notified when back in stock';
  const subtitle = blockSettings.subtitle || "We'll send you an email when this product is available again";
  const buttonText = blockSettings.button_text || 'Notify Me';
  const successMessage = blockSettings.success_message || "Thanks! We'll notify you when this item is back in stock.";
  const placeholderText = blockSettings.placeholder_text || 'Enter your email address';
  const showOnlyWhenSoldOut = blockSettings.show_only_when_sold_out !== false;
  const buttonStyle = blockSettings.button_style || 'primary';
  const inputStyle = blockSettings.input_style || 'standard';

  return (
    <div className='back-in-stock-block'>
      <BackInStock
        title={title}
        subtitle={subtitle}
        buttonText={buttonText}
        successMessage={successMessage}
        placeholderText={placeholderText}
        showOnlyWhenSoldOut={showOnlyWhenSoldOut}
        buttonStyle={buttonStyle}
        inputStyle={inputStyle}
        settings={blockSettings}
      />
    </div>
  );
}

export const ObviyoProductRecommendationsComponent = withBlockEnhancements(
  ({ settings }) => {
    const ObviyoProductRecommendations = lazyComponents['ObviyoProductRecommendations'];

    const blockSettings = settings.block_settings || {};
    blockSettings.id = settings.block_id;

    return <ObviyoProductRecommendations settings={blockSettings} />;
  },
  { enableEvents: false, enableAnimations: false, enablePositioning: false }
);
ObviyoProductRecommendationsComponent.displayName = 'theme__obviyo-recommendations';
